import { useCallback, useEffect, useRef, useState } from "react"
import PersonalDocumentBody from "@/components/PersonalDocumentBody"
import {
  clearPersonalDataConsent,
  savePersonalDataConsent,
} from "@/lib/personalDataConsent"
import { loadPrivacyDocumentWithMeta } from "@/lib/loadPrivacyDocument"
import type { PersonalDocument } from "@/types/personalDocument"

type Props = {
  open: boolean
  onClose: () => void
  onAgreed: () => void
  onDeclined: () => void
}

export default function PersonalDataModal({
  open,
  onClose,
  onAgreed,
  onDeclined,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [scrolledToEnd, setScrolledToEnd] = useState(false)
  const [document, setDocument] = useState<PersonalDocument | null>(null)
  const [revision, setRevision] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)

  const checkScrollEnd = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const threshold = 24
    const atEnd =
      el.scrollTop + el.clientHeight >= el.scrollHeight - threshold
    setScrolledToEnd(atEnd)
  }, [])

  useEffect(() => {
    if (!open) {
      setScrolledToEnd(false)
      return
    }
    let cancelled = false
    setLoading(true)
    void loadPrivacyDocumentWithMeta().then(({ document: doc, revision: rev }) => {
      if (!cancelled) {
        setDocument(doc)
        setRevision(rev)
        setLoading(false)
      }
    })
    return () => {
      cancelled = true
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      setScrolledToEnd(false)
      return
    }
    const t = window.setTimeout(checkScrollEnd, 0)
    return () => window.clearTimeout(t)
  }, [open, checkScrollEnd, document])

  if (!open) return null

  function handleAgree() {
    if (!scrolledToEnd) return
    savePersonalDataConsent(revision ?? 1)
    onAgreed()
    onClose()
  }

  function handleDecline() {
    clearPersonalDataConsent()
    onDeclined()
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/55 p-0 sm:items-center sm:p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90dvh] w-full max-w-lg flex-col rounded-t-2xl border border-white/10 bg-guardians-bg sm:rounded-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="personal-data-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-white/10 px-4 py-3">
          <h2 id="personal-data-title" className="text-base font-semibold">
            {loading ? "Загрузка…" : document?.title}
          </h2>
          {!scrolledToEnd ? (
            <p className="mt-1 text-xs text-amber-200/90">
              Пролистайте текст до конца, чтобы подтвердить согласие
            </p>
          ) : null}
        </div>

        <div
          ref={scrollRef}
          onScroll={checkScrollEnd}
          className="flex-1 overflow-y-auto px-4 py-3 text-sm leading-relaxed text-guardians-muted"
        >
          {loading || !document ? null : (
            <PersonalDocumentBody document={document} />
          )}
        </div>

        <div className="space-y-2 border-t border-white/10 p-4">
          <button
            type="button"
            disabled={!scrolledToEnd || loading || !document}
            onClick={handleAgree}
            className="w-full rounded-xl bg-guardians-accent py-3 text-sm font-medium text-guardians-bg disabled:cursor-not-allowed disabled:opacity-40"
          >
            Я согласен
          </button>
          <button
            type="button"
            onClick={handleDecline}
            className="w-full rounded-xl border border-white/15 py-3 text-sm text-guardians-muted"
          >
            Я не согласен
          </button>
        </div>
      </div>
    </div>
  )
}
